import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon, type IconName } from "./icons";
import { Skeleton } from "./Skeleton";
import { cx } from "./cx";
import { useFocusReturn } from "./focusReturn";
import s from "./overlay.module.css";

export interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  icon?: IconName;
  lead?: ReactNode;
  onSelect: () => void;
}

export interface PaletteGroup {
  id: string;
  heading: string;
  items: readonly PaletteItem[];
  /** Shows skeleton rows instead of items. */
  loading?: boolean;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (q: string) => void;
  groups: readonly PaletteGroup[];
  placeholder?: string;
  label?: string;
  emptyText?: string;
}

/** A combobox in a dialog: type to filter, Up/Down to move, Enter to run, Esc to close. */
export function CommandPalette({ open, onOpenChange, query, onQueryChange, groups, placeholder = "Search members, leads and pages", label = "Search", emptyText = "No results" }: CommandPaletteProps) {
  const listId = useId();
  const focus = useFocusReturn();
  const inputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(0);
  const flat = useMemo(() => groups.flatMap((g) => (g.loading ? [] : g.items)), [groups]);
  const anyLoading = groups.some((g) => g.loading);

  useEffect(() => { setActive(0); }, [query, open]);
  useEffect(() => { if (active >= flat.length && flat.length > 0) setActive(flat.length - 1); }, [flat.length, active]);

  const run = (item: PaletteItem | undefined) => {
    if (!item) return;
    onOpenChange(false);
    item.onSelect();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (flat.length) setActive((a) => (a + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (flat.length) setActive((a) => (a - 1 + flat.length) % flat.length); }
    else if (e.key === "Enter") { e.preventDefault(); run(flat[active]); }
  };

  let index = -1;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.scrim} />
        <Dialog.Content className={s.pal} aria-describedby={undefined} {...focus} onOpenAutoFocus={(e) => { focus.onOpenAutoFocus(); e.preventDefault(); inputRef.current?.focus(); }}>
          <Dialog.Title className="sr-only">{label}</Dialog.Title>
          <div className={s.pin}>
            <Icon name="search" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              autoComplete="off"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={flat[active] ? `${listId}-${flat[active]!.id}` : undefined}
              aria-label={label}
            />
          </div>
          <div className={s.res} id={listId} role="listbox" aria-label={label} aria-busy={anyLoading || undefined}>
            {groups.map((g) => (
              <div key={g.id} role="group" aria-label={g.heading}>
                <div className={s.group}>{g.heading}</div>
                {g.loading
                  ? [0, 1, 2].map((n) => (
                      <div className={s.skRow} key={n}><Skeleton width="55%" height={13} /><Skeleton width="30%" height={11} /></div>
                    ))
                  : g.items.map((item) => {
                      index += 1;
                      const mine = index;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          id={`${listId}-${item.id}`}
                          role="option"
                          aria-selected={mine === active}
                          className={cx(s.opt)}
                          onMouseMove={() => setActive(mine)}
                          onClick={() => run(item)}
                        >
                          {item.lead ?? (item.icon ? <Icon name={item.icon} /> : null)}
                          <span className={s.optText}>{item.label}</span>
                          {item.hint ? <span className={s.k}>{item.hint}</span> : null}
                        </button>
                      );
                    })}
              </div>
            ))}
            {!anyLoading && flat.length === 0 ? <p className={s.none}>{emptyText}</p> : null}
          </div>
          <div className={s.pfoot}>
            <span><span className={s.kbd}>↑↓</span>Move</span>
            <span><span className={s.kbd}>Enter</span>Open</span>
            <span><span className={s.kbd}>Esc</span>Close</span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
